import type {
  LevelsMetadata,
  LevelState,
  StudentLevels,
  UserLevels,
  UserProgress,
} from '../interfaces';

export default function mapUserAndLevels(
  progress: UserProgress,
  levelsMetadata: LevelsMetadata[],
  studentLevels: StudentLevels[]
): UserLevels[] {
  const currentLevel = levelsMetadata.findIndex(
    (l) => l.id === progress.levelId
  );

  const getLevelState = (levelIndex: number, levelId: number) => {
    if (levelIndex === currentLevel) {
      return 'current';
    }

    if (levelIndex > currentLevel) {
      return 'blocked';
    }

    const studentLevel = studentLevels.find((sl) => sl.level.id === levelId);
    if (studentLevel?.levelCompleted) {
      return 'done';
    }

    return 'available';
  };

  const getNumUnitRequiredAndTaken = (levelId: number) => {
    const studentLevel = studentLevels.find((sl) => sl.level.id === levelId);
    return {
      numUnitTaken: studentLevel?.numUnitTaken || 0,
      numUnitRequired: studentLevel?.level.numUnitRequired || 0,
    };
  };

  const getUnitState = (
    levelState: LevelState,
    unitIndex: number
  ): LevelState => {
    if (levelState === 'current') {
      if (unitIndex < progress.unitTaken) {
        return 'done';
      }
      if (unitIndex === progress.unitTaken) {
        return 'current';
      }
      return 'blocked';
    }
    if (levelState === 'blocked') {
      return 'blocked';
    }

    return levelState;
  };

  const getLevelHasProgress = (level: LevelsMetadata) => {
    const studentLevel = studentLevels.find(
      (stuLevel) => stuLevel.level.id === level.id
    );
    if (!studentLevel) return false;

    if (Number(studentLevel.level.levelNum) >= Number(progress.levelNumber))
      return true;

    return studentLevel.numUnitTaken > 0;
  };

  return levelsMetadata.map((level, levelIndex) => {
    const levelState = getLevelState(levelIndex, level.id);
    const { numUnitRequired, numUnitTaken } = getNumUnitRequiredAndTaken(
      level.id
    );

    const hasProgress = getLevelHasProgress(level);
    const levelNumber = level.name.replace(/\D/g, '');

    return {
      ...level,
      state: levelState,
      numUnitRequired,
      numUnitTaken,
      levelNumber,
      hasProgress,
      units: level.units
        .sort((a, b) =>
          +b.name.replace('Unit', '') > +a.name.replace('Unit', '') ? -1 : 1
        )
        .map((unit, unitIndex) => ({
          ...unit,
          index: unitIndex,
          state: getUnitState(levelState, unitIndex),
          unitNumber: unit.name.replace(/\D/g, ''),
        })),
    };
  });
}
